import * as React from 'react';
import {isFragment} from 'react-is';
import {useMergeRefs} from '@floating-ui/react';

import {devAlert} from '@packages/systems/spring-hooks';

import {type HTMLAttributes} from '../__SharedTypes';
import {mergeProps} from '../../utils';
import {emotionStyled} from '@packages/utilities/styled';

/* -------------------------------------------------------------------------------------------------
 * Slot
 * -----------------------------------------------------------------------------------------------*/

export type SlotProps = HTMLAttributes & {
  children: React.ReactNode;
};

/**
 * @component
 */
export const Slot = React.forwardRef<any, SlotProps>(
  ({children, ...slotProps}, forwardedRef) => {
    const childrenArray = React.Children.toArray(children);
    // @ts-expect-error - TS2769 - No overload matches this call.
    const slottable = childrenArray.find(isSlottable);

    if (slottable) {
      // the new element to render is the one passed as a child of `Slottable`
      // @ts-expect-error - TS2339 - Property 'props' does not exist on type 'ReactChild | ReactFragment | ReactPortal'.
      const newElement = slottable.props.children;

      const newChildren = childrenArray.map((child) => {
        if (child === slottable) {
          // because the new element will be the one rendered, we are only interested
          // in grabbing its children (`newElement.props.children`)
          if (React.Children.count(newElement) > 1) return null;
          return React.isValidElement(newElement)
            ? // @ts-expect-error - TS18046 - 'newElement.props' is of type 'unknown'.
              newElement.props.children
            : null;
        } else {
          return child;
        }
      });

      return (
        <SlotClone {...slotProps} ref={forwardedRef}>
          {React.isValidElement(newElement) ? (
            React.cloneElement(newElement, undefined, newChildren)
          ) : (
            <>
              {devAlert(
                true,
                `Please provide a valid child to render. Received ${newElement}`
              )}
            </>
          )}
        </SlotClone>
      );
    }

    return (
      <SlotClone {...slotProps} ref={forwardedRef}>
        {children}
      </SlotClone>
    );
  }
);

Slot.displayName = 'Slot';

const SlotSpan = emotionStyled('span')({
  display: 'contents',
});

/* -------------------------------------------------------------------------------------------------
 * SlotClone
 * -----------------------------------------------------------------------------------------------*/

const SlotClone = React.forwardRef<any, SlotProps>(
  ({children, ...slotProps}, forwardedRef) => {
    /**
     * We need to merge our validElement refs up here, since useMergeRefs is a memoized hook and cannot be called conditionally.
     */
    const childrenRef = React.useMemo(
      () =>
        React.isValidElement(children) && 'ref' in children
          ? (children.ref as React.Ref<unknown>)
          : undefined,
      [children]
    );
    const mergedRefs = useMergeRefs([forwardedRef, childrenRef]);

    /* If a Fragment is passed as the children, we need to wrap it in an element we can spread the Slot props onto */
    if (isFragment(children)) {
      return (
        <SlotSpan {...slotProps} ref={mergedRefs}>
          {children}
        </SlotSpan>
      );
    }

    if (React.isValidElement(children)) {
      return React.cloneElement(children, {
        // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'AnyProps'.
        ...mergeProps(slotProps, children.props),
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        ref: mergedRefs,
      });
    }

    return null;
  }
);

SlotClone.displayName = 'SlotClone';

/* -------------------------------------------------------------------------------------------------
 * Slottable
 * -----------------------------------------------------------------------------------------------*/

export const Slottable = ({children}: {children: React.ReactNode}) => {
  return <>{children}</>;
};

function isSlottable(child: React.ReactElement<any>): boolean {
  return React.isValidElement(child) && child.type === Slottable;
}
